"use client";

import { useMemo } from "react";
import { ArrowRight, BookOpen, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function stripHtml(html?: string): string {
  if (!html) return "";
  return html.replace(/<[^>]*>?/gm, "").trim();
}
function PostsGridConsumer() {
  const {
    posts,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-xs">
            <Skeleton className="h-56 w-full rounded-none" />
            <div className="flex flex-col gap-3 p-6">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-6 w-5/6" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="mt-2 h-9 w-28 rounded-md" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center rounded-2xl border border-border bg-card p-12 text-center shadow-xs">
        <p className="text-base text-foreground mb-4">
          No pudimos cargar los artículos en este momento.
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-2">
          <RefreshCw className="size-4" />
          Reintentar
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="flex flex-col items-center justify-center rounded-2xl border border-border/80 bg-card/60 p-12 text-center shadow-xs">
        <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-accent text-accent-foreground">
          <BookOpen className="size-6" />
        </div>
        <p className="text-base font-medium text-foreground">
          Aún no hay artículos publicados. Vuelve pronto.
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          Estamos preparando nuevos contenidos educativos sobre hongos funcionales y cuidado integral.
        </p>
      </div>;
  }
  return <div className="space-y-12">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {posts.map((post, i) => {
        const cleanExcerpt = stripHtml(post.excerpt);
        const postLink = post.link || "#";
        return <article key={post.id} data-index={i} className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card text-card-foreground shadow-xs transition-shadow duration-300 hover:shadow-md">
              <Link to={postLink} className="relative block aspect-16/10 overflow-hidden bg-muted">
                {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center bg-secondary/50 text-secondary-foreground">
                    <BookOpen className="size-8 stroke-[1.5] text-primary/70" />
                  </div>}
              </Link>

              <div className="flex flex-1 flex-col justify-between p-6">
                <div>
                  <h3 className="font-serif text-xl font-semibold tracking-tight text-foreground transition-colors group-hover:text-primary">
                    <Link to={postLink} className="line-clamp-2">
                      {post.title}
                    </Link>
                  </h3>

                  {cleanExcerpt && <p className="mt-3 text-sm leading-relaxed text-muted-foreground line-clamp-3">
                      {cleanExcerpt}
                    </p>}
                </div>

                <div className="mt-6 pt-4 border-t border-border/70 flex items-center justify-between">
                  <Button asChild variant="link" size="sm" className="px-0 font-medium group-hover:translate-x-0.5 transition-transform">
                    <Link to={postLink} className="inline-flex items-center gap-1.5">
                      Leer artículo
                      <ArrowRight className="size-3.5" />
                    </Link>
                  </Button>
                </div>
              </div>
            </article>;
      })}
      </div>

      {totalPages > 1 && <nav aria-label="Navegación de entradas" className="flex items-center justify-center gap-4 pt-4">
          <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrev} className="gap-1.5">
            <ChevronLeft className="size-4" />
            Anterior
          </Button>

          <span className="text-sm font-medium text-muted-foreground px-2">
            Página {currentPage} de {totalPages}
          </span>

          <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNext} className="gap-1.5">
            Siguiente
            <ChevronRight className="size-4" />
          </Button>
        </nav>}
    </div>;
}
export default function BlogList() {
  const wpQuery = useMemo(() => {
    const wp_query_params = {
      post_type: "post",
      per_page: 9,
      order: "desc",
      orderby: "date"
    };
    return new WP_Query(wp_query_params);
  }, []);
  return <section id="bloglist" data-nav="light" className="bg-background text-foreground py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <header className="max-w-2xl mb-12 sm:mb-16">
          <p className="mt-4 text-base sm:text-lg leading-relaxed text-muted-foreground">
            Hemos preparado varios artículos y guías sobre los hongos adaptógenos y sus aplicaciones para la piel.
          </p>
        </header>

        <PostsProvider wp_query={wpQuery}>
          <PostsGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}